import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';
import { useToggle } from '../../hooks/useToggle';
import PageMeta from '../../components/common/PageMeta';
import Input from '../../components/form/input/InputField';
import debounce from 'lodash.debounce';
import { IFeedback } from '../../types/feedback';
import { feedbacksList } from '../../services/feedback';
import CommonTable, { type ColumnConfig } from '../../components/common/Table';
import { useSimplePagination } from '../../hooks/useSimplePagination';
import { PAGINATION_PER_PAGE } from '../../utils/constants';

const columns: ColumnConfig<IFeedback>[] = [
  {
    key: 'id',
    header: 'ID',
  },
  {
    key: 'name',
    header: 'Name',
  },
  {
    key: 'email',
    header: 'Email',
    className: 'max-w-64 truncate',
    render: (item) => <a href={`mailto:${item.email}`}>{item.email}</a>,
  },
  {
    key: 'experience',
    header: 'Rating',
  },
  {
    key: 'message',
    header: 'Feedback',
  },
  {
    key: 'createdAt',
    header: 'Created At',
    render: (item) => new Date(item.createdAt).toLocaleString(),
  },
];
const Feedbacks = () => {
  const [feedbacks, setFeedbacks] = useState<IFeedback[]>([]);
  const { state: isLoading, on: showLoader, off: hideLoader } = useToggle();
  const [query, setQuery] = useState('');

  const { currentPage, totalPages, onPageChange, setTotalPages } =
    useSimplePagination();

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  };

  const fetchSponsors = async () => {
    if (isLoading) return;
    showLoader();
    const res = await feedbacksList({
      params: { query, page: currentPage, limit: PAGINATION_PER_PAGE },
    }).finally(() => {
      hideLoader();
    });
    setFeedbacks(res.rows);
    setTotalPages(res.count, PAGINATION_PER_PAGE);
  };

  const debouncedResults = useMemo(() => {
    return debounce(fetchSponsors, 500);
    // eslint-disable-next-line
  }, [query, currentPage]);

  useEffect(() => {
    debouncedResults();
    return () => {
      debouncedResults.cancel();
    };
  }, [debouncedResults]);

  return (
    <div>
      <PageMeta title="Feedbacks" description="List of Feedbacks" />
      <PageBreadcrumb pageTitle="Feedbacks" />
      <div className="flex justify-evenly my-4">
        <div className="w-1/2" />
        <div className="w-1/2">
          <Input
            placeholder="Search by Name, Email or Rating"
            value={query}
            onChange={handleChange}
          />
        </div>
      </div>
      <CommonTable
        columns={columns}
        data={feedbacks}
        isLoading={isLoading}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={onPageChange}
        showPagination
      />
    </div>
  );
};

export default Feedbacks;
